import type { CSSProperties } from 'react';
import type { GameBackground } from '@puzzlio/core';

const dotGradient = (color: string) =>
  `radial-gradient(circle at 1.5px 1.5px, ${color} 1.5px, transparent 1.5px), ` +
  `radial-gradient(circle at 6.5px 6.5px, ${color} 1.5px, transparent 1.5px)`;

/**
 * Full dot-pattern background: solid bg color + dot circles on top.
 *
 * A game's page background is one of these, so it can be passed as the `background` from its shared
 * metadata — `dotBgStyle(GAME_CONFIG[gameType])` — rather than as two fields a caller has to pull out
 * and keep in the right order. The two-colour form stays for the backgrounds that aren't a game's:
 * the locked game card, and the mini apps' own chrome.
 */
export function dotBgStyle(source: { background: GameBackground }): CSSProperties;
export function dotBgStyle(bgColor: string, dotColor: string): CSSProperties;
export function dotBgStyle(source: string | { background: GameBackground }, dotColor?: string): CSSProperties {
  const { fill, dot } = typeof source === 'string' ? { fill: source, dot: dotColor as string } : source.background;

  return {
    backgroundColor: fill,
    backgroundImage: dotGradient(dot),
    backgroundSize: '10px 10px',
  };
}

/** Transparent dot overlay — dots only, no background fill. */
export function dotOverlayStyle(dotColor: string): CSSProperties {
  return {
    backgroundImage: dotGradient(dotColor),
    backgroundSize: '10px 10px',
  };
}

// Finer halftone: 1px dots on a 4px tile, versus the 3px dots on a 10px tile above. Same two-dot
// offset layout, so it reads as the same texture family, just tighter.
const smallDotGradient = (color: string) =>
  `radial-gradient(circle at 0.5px 0.5px, ${color} 0.5px, transparent 0.5px), ` +
  `radial-gradient(circle at 2.5px 2.5px, ${color} 0.5px, transparent 0.5px)`;

/** Transparent small-dot overlay — dots only, no background fill. */
export function smallDotOverlayStyle(dotColor: string): CSSProperties {
  return {
    backgroundImage: smallDotGradient(dotColor),
    backgroundSize: '4px 4px',
  };
}

/*
  Checkerboard tile, the flatter cousin of the dot halftone above: two alternating squares rather than
  a dot grid. Keep the two colors close together — this is page texture, and anything with real
  contrast competes with the content sitting on top of it.
*/
export function checkerBgStyle(baseColor: string, squareColor: string, squarePx = 16): CSSProperties {
  return {
    backgroundColor: baseColor,
    backgroundImage: `conic-gradient(${squareColor} 25%, transparent 0 50%, ${squareColor} 0 75%, transparent 0)`,
    backgroundSize: `${squarePx * 2}px ${squarePx * 2}px`,
  };
}

/** The same checkerboard turned 45°, so it reads as diamonds; `tilePx` is one diamond's point-to-point width. */
export function diamondCheckerBgStyle(baseColor: string, diamondColor: string, tilePx = 100): CSSProperties {
  return {
    backgroundColor: baseColor,
    backgroundImage: `conic-gradient(from 45deg, ${diamondColor} 25%, transparent 0 50%, ${diamondColor} 0 75%, transparent 0)`,
    backgroundSize: `${tilePx}px ${tilePx}px`,
  };
}
